import { Injectable } from '@angular/core';
import { CriarPessoaDTO } from '../../dto/pessoa/CriarPessoaDTO';
import { SnackbarService } from '../snackbar/snackbar.service';
import axiosClient from '../../../shared/axios/axios-client';
import { Pessoa } from '../../models/Pessoa';
import { environment } from '../../../environments/environment';
import {
  AtualizarAvaliacaoFisicaRequest,
  AvaliacaoFisicaResponse,
} from '../../view/pessoas/avaliacao-fisica/avaliacao-fisica';
import { AtividadeProposta, AtividadePropostaResumoDTO } from '../../view/pessoas/atividade-proposta/atividade-proposta';
import {
  RegistroConsultaExameRequest,
  RegistroConsultaExameResponse,
} from '../../view/pessoas/consultas-exames/consultas-exames';

@Injectable({
  providedIn: 'root',
})
export class PessoasService {
  private apiUrl = environment.apiUrl;

  constructor(private readonly snackbarService: SnackbarService) {}

  async findAllPessoas(): Promise<Pessoa[]> {
    return await axiosClient
      .get('/pessoa')
      .then((res) => res.data)
      .catch((err) => {
        this.snackbarService.error(err.response?.data?.error || err.message);
        throw err;
      });
  }

  async buscarPessoasPorId(ids: number[]): Promise<Pessoa[]> {
    const idsParam = ids.join(',');
    const url = `${this.apiUrl}/pessoa/lista?ids=${idsParam}`;
    return await axiosClient
      .get<Pessoa[]>(url)
      .then((res) => res.data)
      .catch((err) => {
        this.snackbarService.error(err.response?.data?.error || err.message);
        throw err;
      });
  }

  async buscarPorId(id: number): Promise<Pessoa> {
    const url = `${this.apiUrl}/pessoa/${id}`;
    return await axiosClient
      .get<Pessoa>(url)
      .then((res) => res.data)
      .catch((err) => {
        this.snackbarService.error(err.response?.data?.error || err.message);
        throw err;
      });
  }

  async buscarPessoasPorEmpresaEGrupo(
    empresas: number[] | null,
    grupos: number[] | null,
  ): Promise<Pessoa[]> {
    let params: string[] = [];

    params.push(`empresas=${empresas ? empresas.join(',') : ''}`);
    params.push(`grupos=${grupos ? grupos.join(',') : ''}`);

    const queryString = params.join('&');
    const url = `${this.apiUrl}/pessoa/filtro?${queryString}`;

    return await axiosClient
      .get<Pessoa[]>(url)
      .then((res) => res.data)
      .catch((err) => {
        this.snackbarService.error(err.response?.data?.error || err.message);
        throw err;
      });
  }

  async buscarIdsPessoasBloqueadas(
    empresasIds: number[] = [],
    gruposIds: number[] = [],
  ): Promise<number[]> {
    if (
      (!empresasIds || empresasIds.length === 0) &&
      (!gruposIds || gruposIds.length === 0)
    ) {
      return [];
    }

    let pessoasEmpresa: Pessoa[] = [];
    let pessoasGrupo: Pessoa[] = [];

    if (empresasIds && empresasIds.length > 0) {
      pessoasEmpresa = await this.buscarPessoasPorEmpresaEGrupo(
        empresasIds,
        null,
      );
    }

    if (gruposIds && gruposIds.length > 0) {
      pessoasGrupo = await this.buscarPessoasPorEmpresaEGrupo(null, gruposIds);
    }

    const todasPessoas = [...pessoasEmpresa, ...pessoasGrupo];
    const idsUnicos = Array.from(new Set(todasPessoas.map((p) => p.idPessoa)));

    return idsUnicos.filter((id) => id !== null && id !== undefined);
  }

  async salvarPessoa(pessoa: CriarPessoaDTO): Promise<Pessoa> {
    return await axiosClient
      .post('/pessoa', pessoa)
      .then((res) => res.data)
      .catch((err) => {
        this.snackbarService.error(err.response?.data?.message || err.message);
        throw err;
      });
  }

  async editarPessoa(pessoa: CriarPessoaDTO): Promise<Pessoa> {
    return await axiosClient
      .put(`/pessoa/${pessoa.id}`, pessoa)
      .then((res) => res.data)
      .catch((err) => {
        this.snackbarService.error(err.response?.data?.message || err.message);
        throw err;
      });
  }

  async detalhesTriagem(idPessoa: number): Promise<any> {
    return await axiosClient
      .get(`/pessoa/detalhes-triagem/${idPessoa}`)
      .then((res) => res.data)
      .catch((err) => {
        this.snackbarService.error(err.response?.data?.message || err.message);
        throw err;
      });
  }

  async buscarDadosAvaliacaoFisica(idPessoa: number) {
    return await axiosClient
      .get(`/pessoa/${idPessoa}/avaliacao-fisica`)
      .then((res) => res.data)
      .catch((err) => {
        this.snackbarService.error(err.response?.data?.message || err.message);
        throw err;
      });
  }

  async salvaDadosAvaliacaoFisica(data: AtualizarAvaliacaoFisicaRequest) {

    return await axiosClient
      .put(`/pessoa/${data.idPessoa}/avaliacao-fisica`, data)
      .then((res) => res.data)
      .catch((err) => {
        this.snackbarService.error(err.response?.data?.message || err.message);
        throw err;
      });
  }

  // GET
  async buscarResumoAtividadeProposta(
    idPessoa: number,
    ): Promise<AtividadePropostaResumoDTO> {
      return await axiosClient
        .get(`/pessoa/${idPessoa}/atividades-propostas`)
        .then((res) => res.data?.data as AtividadePropostaResumoDTO)
        .catch((err) => {
          this.snackbarService.error(err.response?.data?.message || err.message);
          throw err;
        });
  }

async atualizarConsultaMedica(
    idPessoa: number,
    idConsulta: number,
    payload: { observacoes?: string | null; dataAgendamento: string | null }
  ): Promise<void> {
    return await axiosClient
      .put(`/pessoa/${idPessoa}/atividades-propostas/consulta-medica/${idConsulta}`, payload)
      .then(() => undefined)
      .catch((err) => {
        this.snackbarService.error(err.response?.data?.message || err.message);
        throw err;
      });
}


async criarConsultaMedica(
    idPessoa: number,
    payload: {
      idPessoa: number;
      observacoesAtividadeProposta?: string | null;
      consultaMedica: {
        dataAgendamento: string | null;
        observacoes?: string | null;
        status: 'Não realizada' | 'Realizada' | string;
      };
    }
  ): Promise<void> {
    return await axiosClient
      .post(`/pessoa/${idPessoa}/atividades-propostas`, payload)
      .then(() => undefined)
      .catch((err) => {
        this.snackbarService.error(err.response?.data?.message || err.message);
        throw err;
      });
  }

  async salvarRegistroConsultaExame(
    payload: RegistroConsultaExameRequest,
  ): Promise<RegistroConsultaExameResponse> {
    return await axiosClient
      .post('/pessoa/registro-consulta-exame', payload)
      .then((res) => res.data?.data as RegistroConsultaExameResponse)
      .catch((err) => {
        this.snackbarService.error(err.response?.data?.message || err.message);
        throw err;
      });
  }

  async listarRegistrosConsultaExame(
    idPessoa: number,
  ): Promise<RegistroConsultaExameResponse[]> {
    return await axiosClient
      .get(`/pessoa/${idPessoa}/registro-consulta-exame`)
      .then((res) => res.data?.data as RegistroConsultaExameResponse[])
      .catch((err) => {
        this.snackbarService.error(err.response?.data?.message || err.message);
        throw err;
      });
  }

  async atualizarRegistroConsultaExame(
    idRegistro: number,
    payload: RegistroConsultaExameRequest,
  ): Promise<RegistroConsultaExameResponse> {
    return await axiosClient
      .put(`/pessoa/registro-consulta-exame/${idRegistro}/atualizar`, payload)
      .then((res) => res.data?.data as RegistroConsultaExameResponse)
      .catch((err) => {
        this.snackbarService.error(err.response?.data?.message || err.message);
        throw err;
      });
  }
}
